import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Crown, Award, Star, Users, TrendingUp, ChevronRight, Lock, Zap, Trophy, Clock, Gift, Target } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// Competition Card with Countdown Timer
const CompetitionCard = ({ competition }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const calculateTime = () => {
      const end = new Date(competition.end_date);
      const now = new Date();
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }
      
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        expired: false
      });
    };
    
    calculateTime();
    const timer = setInterval(calculateTime, 1000);
    return () => clearInterval(timer);
  }, [competition.end_date]);
  
  return (
    <div className="glass-card p-6 border-2 border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-red-500/10">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-orange-500">{competition.title}</h3>
          <p className="text-sm text-muted-foreground">{competition.description}</p>
        </div>
        <div className="bg-orange-500/20 text-orange-500 px-3 py-1 rounded-full text-sm font-bold">
          {competition.target_rank?.toUpperCase()}
        </div>
      </div>
      
      {/* Countdown Timer */}
      {!timeLeft.expired ? (
        <div className="bg-black/30 rounded-xl p-4 mb-4">
          <p className="text-xs text-center text-muted-foreground mb-2">Time Remaining</p>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <p className="text-3xl font-bold text-orange-500">{String(timeLeft.days).padStart(2, '0')}</p>
              <p className="text-xs text-muted-foreground">Days</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-500">{String(timeLeft.hours).padStart(2, '0')}</p>
              <p className="text-xs text-muted-foreground">Hours</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-500">{String(timeLeft.minutes).padStart(2, '0')}</p>
              <p className="text-xs text-muted-foreground">Min</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-500">{String(timeLeft.seconds).padStart(2, '0')}</p>
              <p className="text-xs text-muted-foreground">Sec</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-red-500/20 rounded-xl p-4 mb-4 text-center">
          <p className="text-red-500 font-bold">Competition Ended</p>
        </div>
      )}
      
      {/* Reward */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-yellow-500" />
          <span className="font-bold text-yellow-500">₹{competition.reward?.toLocaleString()}</span>
        </div>
        <div className="text-sm text-muted-foreground">
          {competition.participants || 0} participants
        </div>
      </div>
      {competition.reward_description && (
        <p className="text-xs text-muted-foreground mt-2">{competition.reward_description}</p>
      )}
    </div>
  );
};

const Ranks = () => {
  const { user } = useAuth();
  const [myRank, setMyRank] = useState(null);
  const [allRanks, setAllRanks] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRankData();
  }, []);

  const fetchRankData = async () => {
    try {
      setLoading(true);
      const [myRankRes, allRanksRes, leaderboardRes, competitionsRes] = await Promise.all([
        api.get('/ranks/my-rank'),
        api.get('/ranks/all'),
        api.get('/ranks/leaderboard?limit=10'),
        api.get('/ranks/competitions')
      ]);
      setMyRank(myRankRes.data);
      setAllRanks(allRanksRes.data.ranks);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setCompetitions(competitionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch rank data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rankId) => {
    switch(rankId) {
      case 'starter': return <Star className="w-6 h-6" />;
      case 'silver': return <Award className="w-6 h-6" />;
      case 'gold': return <Trophy className="w-6 h-6" />;
      case 'diamond': return <Zap className="w-6 h-6" />;
      case 'the_king': return <Crown className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-xl">Loading ranks...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="ranks-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-2">Ranking System</h1>
        <p className="text-muted-foreground mb-8">Rise through the ranks and earn rewards</p>

        {/* Not Activated Warning */}
        {!myRank?.is_activated && (
          <div className="glass-card p-6 mb-8 border-2 border-yellow-500/30 bg-yellow-500/10">
            <div className="flex items-center gap-4">
              <Lock className="w-12 h-12 text-yellow-500" />
              <div>
                <h3 className="text-xl font-bold text-yellow-500">Activate to Unlock Ranking</h3>
                <p className="text-muted-foreground">Purchase an activation pack to start your ranking journey!</p>
              </div>
            </div>
          </div>
        )}

        {/* Active Competitions with Timer */}
        {competitions.length > 0 && (
          <div className="mb-8" data-testid="rank-competitions">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Target className="w-6 h-6 text-orange-500" />
              Active Rank Competitions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {competitions.map((comp) => (
                <CompetitionCard key={comp.id} competition={comp} />
              ))}
            </div>
          </div>
        )}

        {/* My Current Rank */}
        {myRank?.is_activated && myRank?.current_rank && (
          <div className="glass-card p-8 mb-8 border-2" style={{ borderColor: myRank.current_rank.badge_color + '50' }}>
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Rank Badge */}
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center"
                style={{ backgroundColor: myRank.current_rank.badge_color + '30' }}
              >
                <div className="text-center" style={{ color: myRank.current_rank.badge_color }}>
                  {getRankIcon(myRank.current_rank_id)}
                  <p className="text-2xl font-bold mt-2">{myRank.current_rank.name}</p>
                </div>
              </div>
              
              {/* Rank Info */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl font-bold mb-2">Your Current Rank</h2>
                <p className="text-xl text-muted-foreground mb-4">{myRank.current_rank.description}</p>
                
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="bg-white/10 rounded-xl px-4 py-2">
                    <p className="text-xs text-muted-foreground">Direct Activated</p>
                    <p className="text-2xl font-bold text-green-500">{myRank.stats.direct_activated}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl px-4 py-2">
                    <p className="text-xs text-muted-foreground">Total Community</p>
                    <p className="text-2xl font-bold text-blue-500">{myRank.stats.total_community}</p>
                  </div>
                  {myRank.current_rank.reward > 0 && (
                    <div className="bg-white/10 rounded-xl px-4 py-2">
                      <p className="text-xs text-muted-foreground">Rank Reward</p>
                      <p className="text-2xl font-bold text-primary">₹{myRank.current_rank.reward.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Progress to Next Rank */}
            {!myRank.is_max_rank && myRank.next_rank && (
              <div className="mt-8 pt-6 border-t border-white/10">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Progress to {myRank.next_rank.name}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Direct Activated Progress */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Direct Activated</span>
                      <span className="font-bold">
                        {myRank.progress.direct_activated.current} / {myRank.progress.direct_activated.required}
                      </span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-4">
                      <div 
                        className="h-4 rounded-full transition-all bg-green-500"
                        style={{ width: `${myRank.progress.direct_activated.percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Need {myRank.progress.direct_activated.needed} more
                    </p>
                  </div>
                  
                  {/* Total Community Progress */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Total Community</span>
                      <span className="font-bold">
                        {myRank.progress.total_community.current} / {myRank.progress.total_community.required}
                      </span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-4">
                      <div 
                        className="h-4 rounded-full transition-all bg-blue-500"
                        style={{ width: `${myRank.progress.total_community.percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Need {myRank.progress.total_community.needed} more
                    </p>
                  </div>
                </div>
              </div>
            )}

            {myRank.is_max_rank && (
              <div className="mt-8 pt-6 border-t border-white/10 text-center">
                <Crown className="w-16 h-16 mx-auto mb-4 text-purple-500" />
                <h3 className="text-2xl font-bold text-purple-500">You've reached the highest rank!</h3>
                <p className="text-muted-foreground">Congratulations, you are The King!</p>
              </div>
            )}
          </div>
        )}

        {/* All Ranks */}
        <div className="glass-card p-6 mb-8" data-testid="all-ranks">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Award className="w-6 h-6 text-primary" />
            All Ranks & Requirements
          </h2>
          
          <div className="space-y-4">
            {allRanks.map((rank, idx) => {
              const isCurrentRank = myRank?.current_rank_id === rank.id;
              const isAchieved = myRank?.current_rank?.level >= rank.level;
              
              return (
                <div 
                  key={rank.id}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                    isCurrentRank 
                      ? 'bg-white/10 border-2' 
                      : isAchieved 
                        ? 'bg-white/5 opacity-90' 
                        : 'bg-white/5 opacity-60'
                  }`}
                  style={{ borderColor: isCurrentRank ? rank.badge_color : 'transparent' }}
                  data-testid={`rank-${rank.id}`}
                >
                  {/* Rank Badge */}
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: rank.badge_color + '30', color: rank.badge_color }}
                  >
                    {getRankIcon(rank.id)}
                  </div>
                  
                  {/* Rank Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold" style={{ color: rank.badge_color }}>
                        {rank.name}
                      </h3>
                      {isCurrentRank && (
                        <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                          Current
                        </span>
                      )}
                      {isAchieved && !isCurrentRank && (
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-500 text-xs rounded-full">
                          ✓ Achieved
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{rank.description}</p>
                  </div>
                  
                  {/* Requirements */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm">
                      <span className="text-green-500 font-bold">{rank.direct_activated}</span> direct activated
                    </p>
                    <p className="text-sm">
                      <span className="text-blue-500 font-bold">{rank.total_community}</span> total community
                    </p>
                    {rank.reward > 0 && (
                      <p className="text-sm text-primary font-bold">
                        ₹{rank.reward.toLocaleString()} reward
                      </p>
                    )}
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="glass-card p-6" data-testid="rank-leaderboard">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Rank Leaderboard
          </h2>
          
          {leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-xl font-semibold">No ranked users yet</p>
              <p className="text-muted-foreground">Be the first to climb the ranks!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((member, idx) => (
                <div 
                  key={member.id}
                  className="flex items-center gap-4 p-4 bg-white/5 rounded-xl"
                >
                  {/* Position */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    idx === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                    idx === 1 ? 'bg-gray-400/20 text-gray-400' :
                    idx === 2 ? 'bg-amber-600/20 text-amber-600' :
                    'bg-white/10'
                  }`}>
                    {idx + 1}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1">
                    <p className="font-semibold">{member.name}</p>
                    <p className="text-sm text-muted-foreground">@{member.username}</p>
                  </div>
                  
                  {/* Rank Badge */}
                  <div 
                    className="px-4 py-2 rounded-full text-sm font-bold"
                    style={{ 
                      backgroundColor: member.badge_color + '20', 
                      color: member.badge_color 
                    }}
                  >
                    {member.rank_name || 'Starter'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Ranks;
